import { createDemoPayload } from "../data/demo";
import type { DashboardPayload, DataGateway, ScenarioId } from "../types";

declare global {
  interface Window {
    google?: {
      script?: {
        run?: GasRunner;
      };
    };
  }
}

type GasRunner = {
  withSuccessHandler(handler: (value: unknown) => void): GasRunner;
  withFailureHandler(handler: (error: { message?: string } | string) => void): GasRunner;
  getDashboardData(scenario: ScenarioId): void;
  updateAppInputs(values: Record<string, number>): void;
};

const CACHE_KEY = "management-dashboard:last-payload:v3";

function storeCache(payload: DashboardPayload) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    // Storage can be unavailable in private browsing. The app remains usable.
  }
}

export function readCache(): DashboardPayload | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DashboardPayload;
    if (!parsed.automation || !parsed.publicFramework) return null;
    return { ...parsed, source: "cache" };
  } catch {
    return null;
  }
}

function gasCall<T>(method: "getDashboardData" | "updateAppInputs", payload: ScenarioId | Record<string, number>): Promise<T> {
  return new Promise((resolve, reject) => {
    const runner = window.google?.script?.run;
    if (!runner) {
      reject(new Error("Apps Scriptブリッジが見つかりません。"));
      return;
    }
    const chain = runner
      .withSuccessHandler((value) => resolve(value as T))
      .withFailureHandler((error) => reject(new Error(typeof error === "string" ? error : error?.message ?? "Apps Scriptでエラーが発生しました。")));
    if (method === "getDashboardData") chain.getDashboardData(payload as ScenarioId);
    else chain.updateAppInputs(payload as Record<string, number>);
  });
}

function createGasGateway(): DataGateway {
  return {
    async getDashboard(scenario) {
      const payload = await gasCall<DashboardPayload>("getDashboardData", scenario);
      payload.source = "gas";
      storeCache(payload);
      return payload;
    },
    updateInputs(values) {
      return gasCall("updateAppInputs", values);
    },
  };
}

function createRemoteGateway(endpoint: string): DataGateway {
  return {
    async getDashboard(scenario) {
      const url = new URL(endpoint);
      url.searchParams.set("action", "dashboard");
      url.searchParams.set("scenario", scenario);
      const response = await fetch(url, { redirect: "follow" });
      if (!response.ok) throw new Error(`データ取得に失敗しました (${response.status})`);
      const payload = await response.json() as DashboardPayload;
      payload.source = "remote";
      storeCache(payload);
      return payload;
    },
    async updateInputs(values) {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "text/plain;charset=utf-8" },
        body: JSON.stringify({ action: "updateInputs", values }),
        redirect: "follow",
      });
      if (!response.ok) throw new Error(`更新に失敗しました (${response.status})`);
      return response.json();
    },
  };
}

function createDemoGateway(): DataGateway {
  let overrides: Record<string, number> = {};
  return {
    async getDashboard(scenario) {
      await new Promise((resolve) => setTimeout(resolve, 280));
      const payload = createDemoPayload(scenario, overrides);
      storeCache(payload);
      return payload;
    },
    async updateInputs(values) {
      overrides = { ...overrides, ...values };
      return { ok: true, updatedAt: new Date().toISOString() };
    },
  };
}

export function createGateway(): DataGateway {
  if (window.google?.script?.run) return createGasGateway();
  const mode = import.meta.env.VITE_DATA_MODE;
  const endpoint = import.meta.env.VITE_GAS_ENDPOINT;
  if (mode === "remote" && endpoint) return createRemoteGateway(endpoint);
  return createDemoGateway();
}
